import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { removeStorageFile } from '@/lib/storage';
import { canWrite, canDelete, WRITE_PERMISSION_DENIED, DELETE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';
import { DEFAULT_TESTIMONIALS } from '@/mocks/testimonials';

// Testimonio de la manada (tabla `testimonials`). Cada uno combina un medio
// (foto o video, normalmente una captura de WhatsApp) con el mensaje del
// cliente y nuestra respuesta de bienvenida.
export interface Testimonial {
  id: string;
  mediaType: 'image' | 'video';
  mediaUrl: string;
  caption: string;
  clientName: string;
  productName: string;
  productSlug: string;
  reply: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
}

// Encabezado editable de la sección (vive en company_settings).
export interface TestimonialSection {
  title: string;
  subtitle: string;
  enabled: boolean;
}

export const DEFAULT_TESTIMONIAL_SECTION: TestimonialSection = {
  title: 'Testimonios de la Manada',
  subtitle: 'Historias reales de clientes que ya hacen parte de la Patyfamilia.',
  enabled: true,
};

function mapRow(row: Record<string, any>): Testimonial {
  return {
    id: row.id,
    mediaType: row.media_type === 'video' ? 'video' : 'image',
    mediaUrl: row.media_url ?? '',
    caption: row.caption ?? '',
    clientName: row.client_name ?? '',
    productName: row.product_name ?? '',
    productSlug: row.product_slug ?? '',
    reply: row.reply ?? '',
    displayOrder: Number(row.display_order ?? 0),
    isActive: row.is_active ?? true,
    createdAt: row.created_at ?? '',
  };
}

// Testimonios activos para la tienda pública. Si la tabla está vacía o falla la
// conexión, devuelve el contenido de prueba para no dejar el carrusel vacío.
export async function fetchPublicTestimonials(): Promise<Testimonial[]> {
  if (!isSupabaseConfigured || !supabase) {
    return DEFAULT_TESTIMONIALS.map((t, i) => ({ ...t, id: `default-${i}`, createdAt: '' }));
  }
  try {
    const { data, error } = await supabase
      .from('testimonials')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Array<Record<string, any>>;
    if (rows.length === 0) {
      return DEFAULT_TESTIMONIALS.map((t, i) => ({ ...t, id: `default-${i}`, createdAt: '' }));
    }
    return rows.map(mapRow);
  } catch (err) {
    console.warn('[testimonials] public fetch failed:', err);
    return DEFAULT_TESTIMONIALS.map((t, i) => ({ ...t, id: `default-${i}`, createdAt: '' }));
  }
}

// Todos los testimonios (incluye inactivos) para el panel de administración.
export async function fetchAllTestimonials(): Promise<Testimonial[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('testimonials')
    .select('*')
    .order('display_order', { ascending: true });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<Record<string, any>>).map(mapRow);
}

// Encabezado público de la sección (whitelist de columnas seguras).
export async function fetchTestimonialsSection(): Promise<TestimonialSection> {
  if (!isSupabaseConfigured || !supabase) return { ...DEFAULT_TESTIMONIAL_SECTION };
  try {
    const { data, error } = await supabase
      .from('company_settings_public')
      .select('testimonios_title, testimonios_subtitle, enable_testimonials')
      .limit(1)
      .maybeSingle();
    if (error || !data) return { ...DEFAULT_TESTIMONIAL_SECTION };
    const row = data as Record<string, any>;
    return {
      title: row.testimonios_title ?? DEFAULT_TESTIMONIAL_SECTION.title,
      subtitle: row.testimonios_subtitle ?? DEFAULT_TESTIMONIAL_SECTION.subtitle,
      enabled: row.enable_testimonials ?? DEFAULT_TESTIMONIAL_SECTION.enabled,
    };
  } catch (err) {
    console.warn('[testimonials] section fetch failed:', err);
    return { ...DEFAULT_TESTIMONIAL_SECTION };
  }
}

// Guarda el encabezado de la sección en la fila única de company_settings.
export async function saveTestimonialsSection(
  input: TestimonialSection,
): Promise<{ error: string | null }> {
  if (!(await canWrite('settings'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  try {
    const payload = {
      testimonios_title: input.title.trim() || DEFAULT_TESTIMONIAL_SECTION.title,
      testimonios_subtitle: input.subtitle.trim(),
      enable_testimonials: input.enabled,
    };
    const { data: existing } = await supabase
      .from('company_settings')
      .select('id')
      .limit(1)
      .maybeSingle();
    if (existing?.id) {
      const { error } = await supabase.from('company_settings').update(payload).eq('id', existing.id);
      if (error) return { error: error.message };
      recordAudit({ entityType: 'settings', entityId: existing.id, action: 'UPDATE', newValue: 'testimonios' });
    } else {
      const { error } = await supabase.from('company_settings').insert(payload);
      if (error) return { error: error.message };
    }
    return { error: null };
  } catch (err) {
    console.warn('[testimonials] section save failed:', err);
    return { error: 'Error de conexión al guardar la sección.' };
  }
}

export interface TestimonialInput {
  id?: string;
  mediaType: 'image' | 'video';
  mediaUrl: string;
  caption: string;
  clientName: string;
  productName: string;
  productSlug: string;
  reply: string;
  displayOrder: number;
  isActive: boolean;
}

export async function saveTestimonial(input: TestimonialInput): Promise<{ error: string | null }> {
  if (!(await canWrite('settings'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  if (!input.mediaUrl.trim()) {
    return { error: 'Debes subir una foto o video (o pegar su enlace) para el testimonio.' };
  }

  const payload = {
    media_type: input.mediaType,
    media_url: input.mediaUrl.trim(),
    caption: input.caption.trim(),
    client_name: input.clientName.trim(),
    product_name: input.productName.trim(),
    product_slug: input.productSlug.trim(),
    reply: input.reply.trim(),
    display_order: input.displayOrder,
    is_active: input.isActive,
  };

  try {
    const auditLabel = input.clientName.trim() || (input.productName.trim() ? `Compró: ${input.productName.trim()}` : 'Testimonio');
    if (input.id) {
      const { error } = await supabase.from('testimonials').update(payload).eq('id', input.id);
      if (error) return { error: error.message };
      recordAudit({ entityType: 'testimonials', entityId: input.id, action: 'UPDATE', newValue: auditLabel });
    } else {
      const { data, error } = await supabase.from('testimonials').insert(payload).select('id');
      if (error) return { error: error.message };
      recordAudit({
        entityType: 'testimonials',
        entityId: (data?.[0] as { id: string } | undefined)?.id ?? '',
        action: 'CREATE',
        newValue: auditLabel,
      });
    }
    return { error: null };
  } catch (err) {
    console.warn('[testimonials] save failed:', err);
    return { error: 'Error de conexión al guardar el testimonio.' };
  }
}

export async function deleteTestimonial(
  id: string,
  mediaUrl: string,
): Promise<{ error: string | null }> {
  if (!(await canDelete('settings'))) return { error: DELETE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  try {
    // Lee el nombre del cliente antes de borrar para dejarlo en la auditoría.
    const { data: existing } = await supabase
      .from('testimonials')
      .select('client_name, product_name')
      .eq('id', id)
      .maybeSingle();
    const { error } = await supabase.from('testimonials').delete().eq('id', id);
    if (error) return { error: error.message };
    // Limpia el archivo de Storage si es propio (ignora URLs externas).
    await removeStorageFile('product-images', mediaUrl);
    const prev = existing as { client_name?: string; product_name?: string } | null;
    const prevLabel = prev?.client_name?.trim()
      || (prev?.product_name?.trim() ? `Compró: ${prev.product_name.trim()}` : null);
    recordAudit({ entityType: 'testimonials', entityId: id, action: 'DELETE', oldValue: prevLabel });
    return { error: null };
  } catch (err) {
    console.warn('[testimonials] delete failed:', err);
    return { error: 'Error de conexión al eliminar el testimonio.' };
  }
}

// Reordena: persiste display_order según el orden recibido.
export async function reorderTestimonials(orderedIds: string[]): Promise<{ error: string | null }> {
  if (!(await canWrite('settings'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };
  try {
    await Promise.all(
      orderedIds.map((id, index) =>
        supabase.from('testimonials').update({ display_order: index + 1 }).eq('id', id),
      ),
    );
    return { error: null };
  } catch (err) {
    console.warn('[testimonials] reorder failed:', err);
    return { error: 'Error de conexión al reordenar.' };
  }
}